<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatTime } from '@chatwoot/utils';
import Avatar from 'next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { formatCount } from '../../helpers/operationsChartHelper';
import { formatDuration } from '../../helpers/durationFormatHelper';

const props = defineProps({
  agents: {
    type: Array,
    default: () => [],
  },
  summaries: {
    type: Array,
    default: () => [],
  },
  timeSummary: {
    type: Array,
    default: () => [],
  },
});

const { t, locale } = useI18n();

const COLLAPSED_ROWS = 8;
const expanded = ref(false);

const summaryFor = id =>
  props.summaries.find(summary => summary.id === Number(id)) || {};
const timeFor = id =>
  props.timeSummary.find(entry => entry.user_id === Number(id)) || {};

// Only agents who actually handled something in the period: an operations panel is about the work
// that happened, not about the size of the roster.
const rows = computed(() =>
  props.agents
    .map(agent => {
      const summary = summaryFor(agent.id);
      return {
        id: agent.id,
        name: agent.available_name || agent.name,
        email: agent.email,
        thumbnail: agent.thumbnail,
        status: agent.availability_status,
        handled: summary.conversationsCount || 0,
        resolved: summary.resolvedConversationsCount || 0,
        firstResponse: summary.avgFirstResponseTime,
        resolution: summary.avgResolutionTime,
        online: timeFor(agent.id).online_duration || 0,
      };
    })
    .filter(row => row.handled > 0)
    .sort((first, second) => second.handled - first.handled)
);

const visibleRows = computed(() =>
  expanded.value ? rows.value : rows.value.slice(0, COLLAPSED_ROWS)
);

const maxHandled = computed(() =>
  Math.max(1, ...rows.value.map(row => row.handled))
);

const duration = seconds => (seconds ? formatTime(Math.round(seconds)) : '—');
</script>

<template>
  <div class="flex flex-col p-4 border rounded-xl border-n-weak bg-n-solid-2">
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ t('OPERATIONS_REPORTS.AGENTS.TITLE') }}
        </h3>
        <p class="mt-0.5 mb-0 text-xs text-n-slate-11">
          {{ t('OPERATIONS_REPORTS.AGENTS.SUBTITLE') }}
        </p>
      </div>
      <Button
        v-if="rows.length > COLLAPSED_ROWS"
        :label="
          expanded
            ? t('OPERATIONS_REPORTS.AGENTS.SHOW_LESS')
            : t('OPERATIONS_REPORTS.AGENTS.SHOW_ALL', { count: rows.length })
        "
        color="slate"
        variant="faded"
        size="xs"
        @click="expanded = !expanded"
      />
    </div>

    <p
      v-if="!rows.length"
      class="py-10 mb-0 text-sm text-center text-n-slate-10"
    >
      {{ t('OPERATIONS_REPORTS.EMPTY') }}
    </p>
    <div v-else class="mt-3 overflow-x-auto">
      <table class="w-full text-sm border-collapse">
        <thead>
          <tr class="text-xs uppercase text-n-slate-10">
            <th class="px-3 py-2 font-medium text-left">
              {{ t('OPERATIONS_REPORTS.AGENTS.AGENT') }}
            </th>
            <th class="px-3 py-2 font-medium text-right whitespace-nowrap">
              {{ t('OPERATIONS_REPORTS.AGENTS.HANDLED') }}
            </th>
            <th class="px-3 py-2 font-medium text-right whitespace-nowrap">
              {{ t('OPERATIONS_REPORTS.AGENTS.RESOLVED') }}
            </th>
            <th class="px-3 py-2 font-medium text-right whitespace-nowrap">
              {{ t('OPERATIONS_REPORTS.AGENTS.FIRST_RESPONSE') }}
            </th>
            <th class="px-3 py-2 font-medium text-right whitespace-nowrap">
              {{ t('OPERATIONS_REPORTS.AGENTS.RESOLUTION') }}
            </th>
            <th class="px-3 py-2 font-medium text-right whitespace-nowrap">
              {{ t('OPERATIONS_REPORTS.AGENTS.ONLINE') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in visibleRows"
            :key="row.id"
            class="border-t border-n-weak"
          >
            <td class="px-3 py-2">
              <div class="flex items-center gap-2.5">
                <Avatar
                  :src="row.thumbnail"
                  :name="row.name"
                  :status="row.status"
                  :size="26"
                  hide-offline-status
                  rounded-full
                />
                <span class="truncate text-n-slate-12">{{ row.name }}</span>
              </div>
            </td>
            <td class="px-3 py-2">
              <div class="flex items-center justify-end gap-2">
                <span class="w-16 h-1.5 rounded bg-n-alpha-1">
                  <span
                    class="block h-full rounded bg-n-blue-9"
                    :style="{ width: `${(row.handled / maxHandled) * 100}%` }"
                  />
                </span>
                <span class="font-medium tabular-nums text-n-slate-12">
                  {{ formatCount(row.handled, locale) }}
                </span>
              </div>
            </td>
            <td class="px-3 py-2 text-right tabular-nums text-n-slate-12">
              {{ formatCount(row.resolved, locale) }}
            </td>
            <td class="px-3 py-2 text-right tabular-nums text-n-slate-11">
              {{ duration(row.firstResponse) }}
            </td>
            <td class="px-3 py-2 text-right tabular-nums text-n-slate-11">
              {{ duration(row.resolution) }}
            </td>
            <td class="px-3 py-2 text-right tabular-nums text-n-slate-10">
              {{ formatDuration(row.online, t) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
